import { useWorkspaces } from '@components/Dashboard/hooks'
import { DropdownMenu } from '@octopus-do/octopus-storybook'
import {
  Archive,
  ArrowRightArrowLeft,
  Bell,
  Clone,
  Leave,
  Pin,
  PinOff,
  Share,
  Trash,
  Undo,
} from '@octopus-do/octopus-storybook/icons'
import type { ProjectItem } from '@store/reducers/dashboard/types'
import type React from 'react'

import { ProjectMoveMenu } from '@/components/ProjectMoveMenu'
import { ProjectStatusMenu } from '@/components/ProjectStatusMenu'
import { PLAN_FREE } from '@/constants/userSubscriptionPlans'
import { useAuth } from '@/hooks'
import { isEnabled } from '../utils'
import { DeleteButton } from './DeleteButton'

interface Props {
  project: ProjectItem
}

const isTeamProject = ({ teamId }: ProjectItem) => teamId !== null

// Чужой проект, которым со мной поделились: владелец им не я, а членство —
// личное, поэтому из него можно только выйти. В командном пространстве доступ
// даёт команда, там выходить не из чего.
const isSharedWithMe = ({ sharedBy, teamId }: ProjectItem) =>
  sharedBy !== null && teamId === null

export const ActionsContent: React.FC<Props> = ({ project }) => {
  const { archived } = project
  const { archive, archive_badge, transfer, remove, move, duplicate } =
    isEnabled(project)
  const { workspaces, current, folderId } = useWorkspaces()
  const { planId } = useAuth()
  const paidPersonal = planId !== PLAN_FREE

  const targetSpaces = workspaces
    .filter((ws) => ws.id !== current?.id)
    .filter((w) => !w.expired)

  const canUnarchive = isTeamProject(project) || paidPersonal
  const shared = isSharedWithMe(project)

  // A template is not in any folder: no pin, move or archive. It is for making
  // projects, and its own team can remove it.
  if (project.template) {
    return (
      <>
        <DropdownMenu.Button
          icon={Clone}
          id="use_template"
          title="Use template"
        />
        <DropdownMenu.Button
          disabled={!remove}
          danger
          icon={Trash}
          id="delete"
          title="Delete"
        />
      </>
    )
  }

  if (archived) {
    return (
      <>
        {canUnarchive && (
          <DropdownMenu.Button icon={Undo} id="unarchive" title="Unarchive" />
        )}
        {!canUnarchive && (
          <DropdownMenu.BadgedButton
            icon={Undo}
            type="pro"
            id="unarchive_upgrade"
            title="Unarchive"
          />
        )}
        {shared && (
          <DropdownMenu.Button
            danger
            icon={Leave}
            id="leave"
            title="Leave project"
          />
        )}
        <DeleteButton allowed={remove} teamProject={isTeamProject(project)} />
      </>
    )
  }

  return (
    <>
      {project.pinned ? (
        <DropdownMenu.Button icon={PinOff} id="unpin" title="Unpin" />
      ) : (
        <DropdownMenu.Button icon={Pin} id="pin" title="Pin to top" />
      )}
      {project.type !== 'report' && (
        <DropdownMenu.Button icon={Share} id="share" title="Share" />
      )}
      <ProjectStatusMenu project={project} />
      {duplicate && (
        <DropdownMenu.Button icon={Clone} id="duplicate" title="Duplicate" />
      )}
      <ProjectMoveMenu
        currentFolder={folderId}
        folders={current?.folders || []}
        disabled={!move}
        workspaces={targetSpaces}
      />
      <DropdownMenu.Button
        disabled={!transfer}
        icon={ArrowRightArrowLeft}
        id="transfer"
        title="Transfer"
      />
      <DropdownMenu.Button
        icon={Bell}
        id="notifications"
        title="Notifications"
      />
      {archive_badge && (
        <DropdownMenu.BadgedButton
          icon={Archive}
          id="archive_upgrade"
          title="Archive"
          type="pro"
        />
      )}
      {!archive_badge && (
        <DropdownMenu.Button
          disabled={!archive}
          icon={Archive}
          id="archive"
          title="Archive"
        />
      )}
      {shared && (
        <DropdownMenu.Button
          danger
          icon={Leave}
          id="leave"
          title="Leave project"
        />
      )}
      <DeleteButton allowed={remove} teamProject={isTeamProject(project)} />
    </>
  )
}
